import { faSpinnerThird as fadSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faRepeat } from '@fortawesome/pro-solid-svg-icons';
import { useFeatureGate } from '@statsig/react-bindings';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useState } from 'react';
import { pick } from 'remeda';

import { Button, Icon, Text } from '@growtherapy/sprout-ui';

import { ContinuityOfCareBanner, ContinuityOfCareBannerProps } from '@grow-therapy-team/seedling-components';

import { EventTypeEnum } from '../../../segment/types';
import { showClientCocBannerGate } from '../../../statsig/gates';
import { mightNeedToRefreshAppointmentListAtom } from '../../schedule-appointment/state';
import { formatShortAppointmentDate, formatTimeRange, getBrowserTimezone } from '../../schedule-appointment/utils';
import { scheduledPatientInformationAtom } from '../../schedule-preview/state';
import { AppointmentLocation } from '../../schedule-preview/types';
import { ProviderDrawerState, drawerStateAtom, selectedClientInfoAtom } from '../../state';
import type { SelectedVisitor } from '../../state';
import { TimelineAppointment, useGetUpcomingAppointmentsQuery } from '../../useGetUpcomingAppointments';
import { useSendClientInfoTabViewTrackingEvent } from '../useSendClientInfoTabViewTrackingEvent';
import { formatUpcomingAppointments, getReferClientLink } from '../utils';
import { AddAppointmentButtonAndReferLink } from './AddAppointmentButtonAndReferLink';
import { RefreshAppointmentsCallout } from './RefreshAppointmentsCallout';

export function UpcomingClientAppointmentsTab({
  upcomingAppointments,
  isLoading,
  onAppointmentClick,
  onClickAddAppointment,
  onRefresh,
  isClientUserAccountPaused,
  referClientLink,
  eapCoverageCard,
  clientFirstName,
}: {
  upcomingAppointments: TimelineAppointment[];
  isLoading?: boolean;
  onAppointmentClick: (appointment: TimelineAppointment) => void;
  onClickAddAppointment: () => void;
  onRefresh?: () => void;
  isClientUserAccountPaused: boolean;
  referClientLink: string;
  eapCoverageCard?: {
    continuityOfCareAcknowledged?: boolean | null;
    remainingSessions?: number | null;
    payorName?: string | null;
    maxSessions?: number | null;
    payorLink?: string | null;
  } | null;
  clientFirstName?: string | null;
}) {
  const [mightNeedToRefreshAppointmentList, setMightNeedToRefreshAppointmentList] = useAtom(
    mightNeedToRefreshAppointmentListAtom,
  );

  const { value: showClientCocBanner } = useFeatureGate(showClientCocBannerGate);

  if (isLoading) {
    return (
      <div className='flex justify-center'>
        <Icon
          aria-hidden={false}
          aria-label='Loading...'
          size='xl'
          className='text-lilac-700 fa-spin'
          icon={fadSpinnerThird}
        />
      </div>
    );
  }

  const bannerProps: ContinuityOfCareBannerProps | null =
    eapCoverageCard && !!eapCoverageCard.payorName && !!clientFirstName
      ? {
          eapCoverageCard: {
            continuityOfCareAcknowledged: eapCoverageCard.continuityOfCareAcknowledged ?? false,
            remainingSessions: eapCoverageCard.remainingSessions ?? 0,
            payorName: eapCoverageCard.payorName,
            maxSessions: eapCoverageCard.maxSessions ?? 0,
            payorLink: eapCoverageCard.payorLink ?? undefined,
          },
          clientFirstName,
        }
      : null;

  if (!upcomingAppointments || upcomingAppointments.length === 0) {
    return (
      <div>
        {bannerProps && showClientCocBanner && <ContinuityOfCareBanner {...bannerProps} />}
        <p> No appointments scheduled.</p>
        <AddAppointmentButtonAndReferLink
          onClickAddAppointment={onClickAddAppointment}
          isClientUserAccountPaused={isClientUserAccountPaused}
          referClientLink={referClientLink}
          className='mt-4'
        />
      </div>
    );
  }

  return (
    <div>
      {bannerProps && showClientCocBanner && <ContinuityOfCareBanner {...bannerProps} />}
      {mightNeedToRefreshAppointmentList && (
        <RefreshAppointmentsCallout
          onClickRefresh={() => {
            setMightNeedToRefreshAppointmentList(false);
            onRefresh?.();
          }}
        />
      )}
      <ul>
        {upcomingAppointments.map((appointment) => {
          const isRecurringAppointment = appointment?.recurringAppointmentGroupingId;
          return (
            <li key={appointment.id}>
              <Button
                use='unstyled'
                className='text-left px-0 py-4 w-full'
                onClick={() => {
                  onAppointmentClick(appointment);
                }}
              >
                <Text className='font-medium'>
                  {formatShortAppointmentDate(appointment?.timeStart)}
                  {isRecurringAppointment && (
                    <Icon aria-hidden={false} aria-label='(recurring appointment)' icon={faRepeat} className='ml-2' />
                  )}
                </Text>
                <Text className='text-neutral-700 font-normal'>
                  <span className='sr-only'>from</span>
                  {formatTimeRange(appointment.timeStart, appointment.timeEnd)} {getBrowserTimezone()}
                </Text>
              </Button>
              <hr className='text-neutral-600' />
            </li>
          );
        })}
      </ul>
      <AddAppointmentButtonAndReferLink
        onClickAddAppointment={onClickAddAppointment}
        isClientUserAccountPaused={isClientUserAccountPaused}
        referClientLink={referClientLink}
        className='mt-4'
      />
    </div>
  );
}

export function UpcomingClientAppointmentsTabWrapper() {
  const [timeStart, _] = useState(new Date().toISOString());
  const selectedWaitingClient = useAtomValue(selectedClientInfoAtom);
  const setDrawerState = useSetAtom(drawerStateAtom);
  const [appointmentInfo, setAppointmentInfo] = useAtom(scheduledPatientInformationAtom);

  const patientShortId = selectedWaitingClient?.patientShortId ?? '';

  const {
    data: upcomingAppointmentsData,
    loading,
    refetch,
  } = useGetUpcomingAppointmentsQuery(patientShortId, timeStart);

  const onAppointmentClick = (appointment: TimelineAppointment) => {
    const { appointmentShortId, patientId, appointmentType, recurringAppointmentGrouping } = appointment;

    const waitingClient = (selectedWaitingClient ?? {}) as SelectedVisitor;
    const presenceFields = pick(waitingClient, [
      'status',
      'visitorUuid',
      'waitingRoomConversationSid',
      'moodCheckinAt',
      'emotions',
      'inSessionAppointmentShortId',
    ] as const);

    setDrawerState(ProviderDrawerState.EDIT_APPOINTMENT);
    setAppointmentInfo({
      ...presenceFields,

      // appointment-specific fields
      appointmentStartTime: appointment.timeStart,
      appointmentEndTime: appointment.timeEnd,
      appointmentLocation: appointment.inOffice,
      appointmentId: appointment.id,
      patientId: patientId?.toString() || '',
      appointmentType,
      recurringAppointmentGrouping,
      appointmentShortId,
      patientShortId: patientShortId,
      name: selectedWaitingClient?.name ?? appointmentInfo?.name,

      // seedling-specific fields
      patientName: selectedWaitingClient?.name ?? appointmentInfo?.name ?? '',
      pronouns: upcomingAppointmentsData?.patientChart?.pronouns ?? '',
      recurringAppointmentGroupingId: recurringAppointmentGrouping?.recurringAppointmentGroupingId ?? '',
      indefinite: recurringAppointmentGrouping?.indefinite ?? false,
      start: appointment.timeStart,
      end: appointment.timeEnd,
      virtual: appointment.inOffice === AppointmentLocation.Virtual,
      weeksBetween: recurringAppointmentGrouping?.weeksBetween ?? 0,
      numRecurrences: recurringAppointmentGrouping?.appointments?.length ?? 0,
    });
  };

  const appointments = upcomingAppointmentsData?.currentProvider?.providerAppointments?.edges;
  const isClientUserAccountPaused = !!upcomingAppointmentsData?.patientChart?.clientUser?.activePauseRecord;

  const upcomingAppointments = formatUpcomingAppointments(appointments);
  const eapCoverageCard = upcomingAppointmentsData?.patientChart?.clientUser?.eapCoverageCard;
  const clientFirstName = upcomingAppointmentsData?.patientChart?.firstName;

  useSendClientInfoTabViewTrackingEvent(patientShortId, EventTypeEnum.SCHEDULE_TAB);

  return (
    <UpcomingClientAppointmentsTab
      upcomingAppointments={upcomingAppointments}
      isLoading={loading}
      onAppointmentClick={onAppointmentClick}
      onClickAddAppointment={() => {
        setDrawerState(ProviderDrawerState.ADD_APPOINTMENT);
      }}
      onRefresh={refetch}
      isClientUserAccountPaused={isClientUserAccountPaused}
      referClientLink={getReferClientLink(patientShortId)}
      eapCoverageCard={eapCoverageCard}
      clientFirstName={clientFirstName}
    />
  );
}
